import React, { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label } from "reactstrap";
import api from "@/services/api";
import NotificationAlertOptions from "@/layouts/Alerts";
import { errorPayload } from "@/services/caughtError";
import getErrorMessage from "@/utils/getErrorMessage";

/**
 * SCRUM-1918: the post's editor and reviewer, picked from the people list and
 * stored as user ids (the server side is SCRUM-1917).
 *
 * The list is `GET /api/users/byline-people` (id and name of every live user,
 * any signed-in role; SCRUM-1923): `GET /api/users` is administrator-only, and
 * content editors open this screen too (Dirk, SCRUM-1918 c24726). The public
 * author list `get-all-authors` is not used here, because it keeps soft-deleted
 * authors for blog bylines (SCRUM-1898). A failed read leaves the list empty and
 * the rest of the form keeps working, but it is reported in an error toast,
 * and a stored id then reads "People list not loaded" rather
 * than "Unavailable user", which would say the person was deleted (SCRUM-2001).
 *
 * `onEditorChange` / `onReviewerChange` receive the chosen user id, or `null`
 * for "none". A stored id that is not in the list (a soft-deleted user) is shown
 * as its own option, so the select reflects what is stored.
 */
type BylinePeopleFieldsProps = {
  editor: string | null;
  reviewer: string | null;
  onEditorChange: (id: string | null) => void;
  onReviewerChange: (id: string | null) => void;
};

export default function BylinePeopleFields({
  editor,
  reviewer,
  onEditorChange,
  onReviewerChange,
}: BylinePeopleFieldsProps) {
  const [people, setPeople] = useState<{ _id: string; name?: string }[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get("/api/users/byline-people")
      .then((res) => {
        if (live && Array.isArray(res.data)) setPeople(res.data);
      })
      .catch((err) => {
        if (!live) return;
        setLoadFailed(true);
        NotificationAlertOptions(
          "error",
          `The people list could not be loaded: ${getErrorMessage(errorPayload(err))}`
        );
      });
    return () => {
      live = false;
    };
  }, []);

  const field = (
    id: string,
    label: string,
    value: string | null,
    onChange: (id: string | null) => void
  ) => (
    <FormGroup className="row">
      <Label className="form-control-label" md="2" for={id}>
        {label}
      </Label>
      <Col md="6">
        <Input
          id={id}
          name={id}
          type="select"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">None</option>
          {value && !people.some((p) => p._id === value) ? (
            <option value={value}>
              {loadFailed ? "People list not loaded" : "Unavailable user"}
            </option>
          ) : null}
          {people.map((p) => (
            <option key={p._id} value={p._id}>
              {p.name || p._id}
            </option>
          ))}
        </Input>
      </Col>
    </FormGroup>
  );

  return (
    <>
      {field("postEditor", "Editor", editor, onEditorChange)}
      {field("postReviewer", "Reviewer", reviewer, onReviewerChange)}
    </>
  );
}
